import { PanelDropdownItemDto } from '@oncolens/oncolens-ui/lib/components/BiomarkerResults/BiomarkerResults.api';
import React, { useEffect, useState } from 'react';
import { ImSpinner2 } from 'react-icons/im';
import shallow from 'zustand/shallow';
import { createPanels } from '../hooks/useBiomarkers';
import { useGetPanelsByPatient } from '../hooks/useGetPanelsByPatient';
import useRequestFormStore from '../store';
import BiomarkerPanelSelector from './BiomarkerPanelSelector';
// Interfaces for interacting with data

export interface BiomarkerItem {
  id: string;
  biomarkerName: string | undefined;
  value?: string;
  interpretation?: string;
  comments?: string;
}

// Interfaces for context provider

interface BiomarkerActiveIds {
  selectedPanelId: string | null;
  isEditingPanel: boolean;
  biomarkerId: string | null;
}

interface BiomarkerFormIds {
  patientId: string | null;
}

interface BiomarkerContextValues {
  biomarkerPanels: PanelDropdownItemDto[];
  setBiomarkerPanels: React.Dispatch<React.SetStateAction<PanelDropdownItemDto[]>>;
  isInAutosave: boolean;
  activeIds: BiomarkerActiveIds;
  formIds: BiomarkerFormIds;
  setActiveIds: React.Dispatch<React.SetStateAction<BiomarkerActiveIds>>;
  persistToForm: (items: PanelDropdownItemDto[]) => void;
}

const BiomarkerContext = React.createContext({
  biomarkerPanels: [] as PanelDropdownItemDto[],
  setBiomarkerPanels: (
    state:
      | PanelDropdownItemDto[]
      | ((previousState: PanelDropdownItemDto[]) => Record<string, unknown>)
  ) => {},
  isInAutosave: false,
  activeIds: {} as BiomarkerActiveIds,
  formIds: {} as BiomarkerFormIds,
  setActiveIds: (
    state: BiomarkerActiveIds | ((previousState: BiomarkerActiveIds) => Record<string, unknown>)
  ) => {},
  persistToForm: () => {},
} as BiomarkerContextValues);

export const useBiomarkerContext = () => React.useContext(BiomarkerContext);

const PatientBiomarkerForm = () => {
  const formKey = `panels`;
  const [updateField, updated, patientId] = useRequestFormStore(
    (store) => [store.updateField, store.panels, store.patient?.id],
    shallow
  );
  const [biomarkerPanels, setBiomarkerPanels] = useState((updated || []) as PanelDropdownItemDto[]);
  const [activeIds, setActiveIds] = useState({
    selectedPanelId: null,
    isEditingPanel: false,
    biomarkerId: null,
  } as BiomarkerActiveIds);
  const formIds = {
    patientId,
  };
  // disable autosave (avoid hydrating for edit request)
  const isInAutosave = false;

  useEffect(() => {
    if (updated) {
      setBiomarkerPanels(updated);
    }
  }, [updated]);

  const { isLoading } = useGetPanelsByPatient(patientId, {
    enabled: isInAutosave,
    onSuccess: (data: any) => {
      updateField('panels', data);
    },
  });

  const contextValues = {
    biomarkerPanels,
    setBiomarkerPanels,
    formIds,
    isInAutosave,
    activeIds,
    setActiveIds,
    persistToForm: (values: PanelDropdownItemDto[]) => {
      updateField(formKey, values, { markDirty: false });
    },
  };

  return (
    <BiomarkerContext.Provider value={contextValues}>
      <BiomarkerPanelSelector />
    </BiomarkerContext.Provider>
  );
};

PatientBiomarkerForm.save = async (episodeId: string, biomarkers: any) => {
  return createPanels(episodeId, biomarkers);
};

export { PatientBiomarkerForm as default };
